import type { AsyncDataReloaderT } from '@dr.pogodin/react-global-state';
import { time } from '@dr.pogodin/react-utils';

import { listAritsts } from 'services/artists';

import {
  type ArtistListT,
  type ArtistStatusT,
  type ArtistT,
  DEFAULT_ARTIST_PAGE_SIZE,
  areEqualArrays,
  getId,
} from 'utils';

import { useAsyncData, useGlobalStateObject } from 'utils/global-state';

type UseArtistsOptionsT = {
  /** Result page size. */
  pageSize?: number;

  /** Index of the single result page to retrieve. Without it the hook will work
   *  in the "progressive" loading mode. */
  singlePage?: number;

  /** Artist status filter. Mind that users without the "USER_MANAGEMENT"
   *  permissions may only query "Active" artists, and they must to pass
   *  "Active" value into this field. */
  status?: ArtistStatusT | ArtistStatusT[];

  /** Allows to search artists by name. */
  textSearch?: string;
};

type UseArtistListResT = {
  items: ArtistT[];
  loadMore: (() => void) | undefined;
  loading: boolean;
  numPages: number | undefined;
  reload: AsyncDataReloaderT<ArtistListT>;
};

/**
 * Returns "true" if the given `list` of artists have been retrieved with
 * options incompatible with the given ones, and thus should be re-loaded.
 * Returns "false" otherwise, meaning that the `list` can be returned as
 * the result of the query with provided options.
 */
function isStale(list: ArtistListT, {
  pageSize,
  singlePage,
  status,
  textSearch = '',
}: UseArtistsOptionsT): boolean {
  if (pageSize !== list.pageSize || textSearch !== list.textSearch) return true;

  if (singlePage === undefined) {
    if (list.singlePage) return true;
  } else if (
    singlePage !== list.lastPage || list.items.length !== list.pageSize
  ) return true;

  if (Array.isArray(status) && Array.isArray(list.status)) {
    if (!areEqualArrays(status, list.status)) return true;
  } else if (status !== list.status) return true;

  return false;
}

/**
 * Gets the array of artists registered in the system.
 */
export function useArtistList({
  pageSize = DEFAULT_ARTIST_PAGE_SIZE,
  singlePage,
  status,
  textSearch,
}: UseArtistsOptionsT): UseArtistListResT {
  // As an optimization, whenever we get a page of artist records we also
  // update corresponding records into the artist records map, stored at
  // a different segment of the global state.
  const gs = useGlobalStateObject();
  const updateArtistMap = (artists: ArtistT[]) => {
    const now = Date.now();
    for (const artist of artists) {
      const path = `artists.map.${getId(artist)}` as const;
      gs.set(path, { ...gs.get(path), data: artist, timestamp: now });
    }
  };

  const statusStr = Array.isArray(status) ? status.join(';') : status;

  // Note: this loader always loads a single page of results (which might be
  // the first page in the "progressive loading" mode). For loading subsequent
  // pages in the "progressive loading" mode we have a dedicated loader defined
  // below.
  const e = useAsyncData('artists.list', (old): ArtistListT | Promise<ArtistListT> => {
    if (old && !isStale(old, {
      pageSize,
      singlePage,
      status,
      textSearch,
    })) return old;

    return (async () => {
      const res = await listAritsts({
        getTotalCount: singlePage !== undefined,
        legacyTextSearch: textSearch,
        page: singlePage ?? 0,
        pageSize,
        status,
      });

      updateArtistMap(res.artists);

      return {
        hasMore: res.artists.length === pageSize,
        items: res.artists,
        lastPage: singlePage ?? 0,
        numPages: res.totalCount && Math.ceil(res.totalCount / pageSize),
        pageSize,
        singlePage: singlePage !== undefined,
        status,
        textSearch,
      };
    })();
  }, {
    deps: [pageSize, singlePage, statusStr, textSearch],
    maxage: time.YEAR_MS,
  });

  return {
    items: e.data?.items ?? [],
    loadMore: e.data && e.data.hasMore && !e.loading ? () => {
      void e.reload(async (old): Promise<ArtistListT> => {
        if (!old) throw Error('Internal error');

        const res = await listAritsts({
          legacyTextSearch: textSearch,
          page: old.lastPage + 1,
          pageSize,
          status,
        });

        updateArtistMap(res.artists);

        const items = [...old.items];
        const itemMap: Record<string, number> = {};
        for (let idx = 0; idx < items.length; ++idx) {
          itemMap[getId(items[idx])] = idx;
        }

        for (const artist of res.artists) {
          const artistId = getId(artist);

          const idx = itemMap[artistId];
          if (idx === undefined) items.push(artist);
          else items[idx] = artist;
        }

        return {
          hasMore: res.artists.length === pageSize,
          items,
          lastPage: old.lastPage + 1,
          numPages: undefined,
          pageSize,
          singlePage: false,
          status,
          textSearch,
        };
      });
    } : undefined,
    loading: e.loading,
    numPages: e.data?.numPages,
    reload: e.reload,
  };
}
